import classNames from "classnames";
import type { ReactNode } from "react";

export type ActionBarAction =
  | {
      id: string;
      href: string;
      download?: boolean;
      content: ReactNode;
    }
  | {
      id: string;
      onAction?: () => void;
      content: ReactNode;
    };

export interface ActionBarProps {
  primaryActions?: ActionBarAction[];
  secondaryActions?: ActionBarAction[];
  onAnyAction?: () => void;
}

export function ActionBar(props: ActionBarProps) {
  if (!props.primaryActions?.length && !props.secondaryActions?.length) {
    return null;
  }

  return (
    <div className="item--actions">
      {props.primaryActions?.length ? (
        <div className="flex flex-grow flex-row flex-nowrap items-center justify-start space-x-3">
          {props.primaryActions.map((action) => {
            return (
              <Action
                {...action}
                key={action.id}
                className="item--actions--primary"
                onAnyAction={props.onAnyAction}
              />
            );
          })}
        </div>
      ) : null}
      {props.secondaryActions?.length ? (
        <div className="flex flex-grow flex-row flex-nowrap items-center justify-end space-x-3">
          {props.secondaryActions.map((action) => {
            return (
              <Action
                {...action}
                key={action.id}
                className="item--actions--secondary"
                onAnyAction={props.onAnyAction}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export type ActionProps = ActionBarAction & {
  className?: string;
  onAnyAction?: () => void;
};

function Action(props: ActionProps) {
  if ("href" in props) {
    return (
      <div
        className={classNames(
          "flex items-center justify-center",
          props.className,
        )}>
        <a
          data-test-id="action"
          data-id={props.id}
          href={props.href}
          onClick={() => props.onAnyAction?.()}
          download={props.download}
          target="_blank"
          rel="noopener noreferrer"
          className="item--actions--button">
          {props.content}
        </a>
      </div>
    );
  }

  if (props.id === "item-title") {
    return <div>{props.content}</div>;
  }

  if (
    props.id === "shopify-buy-now" ||
    props.id === "shopify-add-to-cart" ||
    props.id === "shopify-cart"
  ) {
    return (
      <div
        className={classNames(
          "flex items-center justify-center",
          props.className,
        )}>
        {props.content}
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "flex items-center justify-center",
        props.className,
      )}>
      <button
        data-test-id="action"
        data-id={props.id}
        type="button"
        tabIndex={0}
        className="item--actions--button"
        onClick={() => {
          props.onAnyAction?.();
          props.onAction?.();
        }}>
        {props.content}
      </button>
    </div>
  );
}
